import { controlPreloadIdentity, type ControlLookupVariant } from './preload-controls.js'
import type * as Types from '@aeppic/types'
import { Form } from '../../model/form.js'

function isForm(value: unknown): value is Form {
  return !!value && typeof (value as Form).getPlaceholders === 'function' && typeof (value as Form).id === 'string'
}

/**
 * Resolves every placeholder control for a form version in one turn and
 * remembers the result for `form.id` / `form.v` / placeholder offset.
 */
export class Forms {
  private _cache = new Map<string, Map<number, Types.Document | null>>()
  private _pending = new Map<string, Promise<Map<number, Types.Document | null>>>()

  private _generation = 0
  private _loadedForms = new Set<Form>()
  constructor(private _aeppic) {}

  clearControlCache() {
    this._generation++
    this._cache.clear()
    this._pending.clear()
    for (const form of this._loadedForms) form.clearPreloadedControls?.()
    this._loadedForms.clear()
  }

  cacheKey(form: { id: string, v: string }, variant: ControlLookupVariant = {}) {
    return controlPreloadIdentity(this._aeppic, form, variant)
  }

  preloadedControl(form: { id: string, v: string }, placeholderOffset: number, variant: ControlLookupVariant = {}): Types.Document | null | undefined {
    const cached = this._cache.get(this.cacheKey(form, variant))
    if (!cached || !cached.has(placeholderOffset)) return undefined
    return cached.get(placeholderOffset)
  }

  async preloadControlsForForm(formOrReference: Form | Types.IdOrReference, variant: ControlLookupVariant = {}): Promise<Map<number, Types.Document | null>> {
    const form = isForm(formOrReference) ? formOrReference : await this._aeppic.getForm(formOrReference)
    if (!form) return new Map()
    this._loadedForms.add(form)
    const key = this.cacheKey(form, variant)
    const generation = this._generation
    const cached = this._cache.get(key)
    if (cached) {
      form.setPreloadedControls(cached, variant, key)
      return cached
    }
    const pending = this._pending.get(key)
    if (pending) {
      const resolved = await pending
      if (generation === this._generation) form.setPreloadedControls(resolved, variant, key)
      return resolved
    }
    const loading = this._resolveAll(form, variant)
    this._pending.set(key, loading)
    try {
      const resolved = await loading
      if (generation !== this._generation) return resolved
      this._cache.set(key, resolved)
      form.setPreloadedControls(resolved, variant, key)
      return resolved
    } finally {
      if (this._pending.get(key) === loading) this._pending.delete(key)
    }
  }

  async prefetchControlsForForm(form: Form | Types.IdOrReference, variants: ControlLookupVariant[]) {
    const results = new Map<string, Map<number, Types.Document | null>>()
    for (const variant of variants) {
      const resolved = isForm(form) ? form : await this._aeppic.getForm(form)
      const key = this.cacheKey(resolved, variant)
      if (!results.has(key)) results.set(key, await this.preloadControlsForForm(resolved, variant))
    }
    return results
  }

  private _resolveAll(form: Form, variant: ControlLookupVariant): Promise<Map<number, Types.Document | null>> {
    return this._aeppic.Controls.resolveForForm(form, variant)
  }
}
